import * as React from 'react'

import { now } from '@espoon-voltti/ts-libs/lib/time'

import { Org } from '@espoon-voltti/nuora-domain/applicants'
import {
  GrantPeriod,
  GrantPeriodType
} from '@espoon-voltti/nuora-domain/grants'

import {
  ProjectGrantDraftApp,
  GrantApp,
  SentApp,
  SigningApp,
  ValidDraftApp,
  ValidGeneralGrantDraftApp,
  GeneralGrantDraftApp
} from '../domain'

import { appsApi, grantPeriodApi, orgApi } from './api'
import { handleError } from '../error-handling'

export const useStore = () => {
  const [apps, setApps] = React.useState<GrantApp[]>([])
  const [appsLoading, setAppsLoading] = React.useState<boolean>(true)

  const [grantPeriods, setGrantPeriods] = React.useState<GrantPeriod[]>([])
  const [grantPeriodsLoading, setGrantPeriodsLoading] =
    React.useState<boolean>(true)

  const [orgs, setOrgs] = React.useState<Org[]>([])
  const [orgsLoading, setOrgsLoading] = React.useState<boolean>(true)

  React.useEffect(() => {
    orgApi
      .getAll()
      .then(setOrgs)
      .catch(handleError)
      .finally(() => setOrgsLoading(false))
  }, [])

  React.useEffect(() => {
    grantPeriodApi
      .get()
      .then(setGrantPeriods)
      .catch(handleError)
      .finally(() => setGrantPeriodsLoading(false))
  }, [])

  React.useEffect(() => {
    setAppsLoading(true)
    Promise.all(orgs.map((x) => appsApi.getByOrg(x.id)))
      .then((xs) =>
        setApps(xs.reduce((acc, current) => acc.concat(current), []))
      )
      .catch(handleError)
      .finally(() => setAppsLoading(false))
  }, [orgs])

  const loading = appsLoading || grantPeriodsLoading || orgsLoading

  const activePeriods = (): GrantPeriod[] =>
    grantPeriods.filter(isPubliclyActive)

  const appById = (appId: string) => apps.find((x) => x.id === appId)

  const appsByOrg = (orgId: string) =>
    apps.filter((x) => x.applicant.org.id === orgId)

  const createDraft = (
    orgId: string,
    grantPeriodId: string,
    grantPeriodType: GrantPeriodType
  ): Promise<ProjectGrantDraftApp | GeneralGrantDraftApp> =>
    appsApi.createDraft(orgId, grantPeriodId, grantPeriodType).then((x) => {
      setApps((state) => state.concat(x))
      return x
    })

  const deleteApp = (appId: string) =>
    appsApi.deleteById(appId).then(() => {
      setApps((state) => state.filter((x) => x.id !== appId))
    })

  const orgById = (orgId: string) => orgs.find((x) => x.id === orgId)!

  const setApp = (app: GrantApp) =>
    setApps((state) => state.map((x) => (x.id === app.id ? app : x)))

  const signApp = (
    orgSnapshot: Org,
    signable: ValidDraftApp | ValidGeneralGrantDraftApp | SigningApp
  ): Promise<SigningApp | SentApp> =>
    signable.state === 'DRAFT'
      ? appsApi
          .sendForSigning(orgSnapshot, signable)
          .then(() => appsApi.sign(signable.id))
      : appsApi.sign(signable.id)

  return {
    activePeriods,
    appById,
    appsByOrg,
    createDraft,
    deleteApp,
    grantPeriods,
    loading,
    orgById,
    orgs,
    saveDraft: appsApi.saveDraft,
    setApp,
    signApp
  }
}

type StoreApi = ReturnType<typeof useStore>
const Ctx = React.createContext<StoreApi | null>(null)
export const Store = {
  Provider: Ctx.Provider,
  use: () => {
    const ctx = React.useContext(Ctx)
    if (ctx === null) {
      throw new Error('Must be wrapped within Store.Provider')
    }
    return ctx
  }
}

const isPubliclyActive = (p: GrantPeriod) =>
  p.state === 'ACTIVE' && now().isWithInRange(p.startDatetime, p.endDatetime)
